"use client";

import { useState } from "react";
import Link from "next/link";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";
import { useDemoTheme, type FooterTopic } from "@/context/DemoThemeContext";

function formatNumber(number: string) {
  return number.replace(/\D/g, "");
}

function SubtopicContent({ content, textColor }: { content: string; textColor: string | null }) {
  return (
    <div className="text-body mb-2.5" style={{ color: textColor ?? "#ffffff" }}>
      <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeSanitize]}>
        {content}
      </Markdown>
    </div>
  );
}

function SubtopicList({ topic, textColor, open }: { topic: FooterTopic; textColor: string | null; open: boolean }) {
  return (
    <>
      {topic.subtopic
        .slice()
        .sort((a, b) => a.order - b.order)
        .map((sub) => (
          <div
            key={sub.id}
            className={`w-full relative overflow-hidden transition-colors duration-300 ease-in-out ${open ? "max-h-40 opacity-100 mt-5" : "max-h-0 opacity-0"}`}
          >
            {sub.link && (
              <Link
                href={
                  sub.is_telephone
                    ? `tel:${formatNumber(sub.link)}`
                    : sub.is_email
                      ? `mailto:${sub.link}`
                      : sub.link
                }
                target={sub.new_tab ? "_blank" : "_self"}
                className="w-full h-full absolute top-0 left-0"
                style={{ color: textColor ?? "#ffffff" }}
              />
            )}
            <SubtopicContent content={sub.content} textColor={textColor} />
          </div>
        ))}
    </>
  );
}

export default function LinkFooter() {
  const { footer } = useDemoTheme();
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});

  const toggle = (title: string) =>
    setOpenSections((prev) => ({ ...prev, [title]: !prev[title] }));

  const topics = (footer?.topic ?? []).slice().sort((a, b) => a.order - b.order);
  const textColor = footer?.text_color ?? null;

  if (!topics.length) return null;

  return (
    <>
      {/* Desktop: 5-column grid */}
      <div className="hidden lg:grid grid-cols-5 gap-8">
        {topics.map((topic) => (
          <div key={topic.id}>
            <p className="text-lede font-bold mb-5" style={{ color: textColor ?? "#ffffff" }}>
              {topic.title}
            </p>
            {topic.subtopic
              .slice()
              .sort((a, b) => a.order - b.order)
              .map((sub) => (
                <div key={sub.id} className="w-full mb-2.5 relative">
                  {sub.link && (
                    <Link
                      href={
                        sub.is_telephone
                          ? `tel:${formatNumber(sub.link)}`
                          : sub.is_email
                            ? `mailto:${sub.link}`
                            : sub.link
                      }
                      target={sub.new_tab ? "_blank" : "_self"}
                      className="w-full h-full absolute top-0 left-0"
                      style={{ color: textColor ?? "#ffffff" }}
                    />
                  )}
                  <SubtopicContent content={sub.content} textColor={textColor} />
                </div>
              ))}
          </div>
        ))}
      </div>

      {/* Mobile: accordion */}
      <div className="block lg:hidden mb-5">
        {topics.map((topic) => (
          <div key={topic.id} className="cursor-pointer mb-5">
            <div className="w-full" onClick={() => toggle(topic.title)}>
              <p className="text-body font-bold mb-5" style={{ color: textColor ?? "#ffffff" }}>
                {topic.title}
              </p>
              <div className="w-full h-px" style={{ backgroundColor: textColor ?? "#ffffff" }} />
            </div>
            <SubtopicList topic={topic} textColor={textColor} open={!!openSections[topic.title]} />
          </div>
        ))}
      </div>
    </>
  );
}
